<script setup lang="ts">
import { BriefcaseIcon } from '@heroicons/vue/outline'

import {
    accountLinksLegal,
    getHiddenLegalNavigationSections,
    LegalAccountNavigationStore,
    LegalAccountPage,
    navigateTo,
    navigationItemsLegal,
    useLegalAccountNavigationState,
} from '../../../utilities/account/useAccountNavigationLegal'
import { computed, onMounted, ref, watch } from 'vue'
import { isEcspComplianceProfileInformationMissingForInvestment } from '../../../utilities/account/useEcspRegulatory'
import { getPlatformSettings } from '../../../utilities/usePlatformSettings'
import { isEsacLegalComplianceProfileComplete } from '../../wizard/ESAC/form/forms'
import AccountNavigation from '../AccountNavigation.vue'
import { useLegalAccountDataState } from '../../../utilities/account/useAccountDataLegal'
import { useForm } from 'vee-validate'
import { showWalletLink, showOrdersLink } from '../../../utilities/useLinks'
import { getLegalAccountValidationSchema } from '../../../utilities/account/useAccountFormLegal'
import { i18n } from '../../../localization'

const navigationState: LegalAccountNavigationStore = useLegalAccountNavigationState() as LegalAccountNavigationStore
const legalAccountState = useLegalAccountDataState()
const legalAccount = legalAccountState.legalProfile

const formErrors = ref({})
const complianceProfileComplete = ref(false)

onMounted(() => {
    setFormErrors()
    updateComplianceProfileIsComplete()
})

async function updateComplianceProfileIsComplete() {
    if (getPlatformSettings().compliance_profile === 'esac') {
        complianceProfileComplete.value = await isEsacLegalComplianceProfileComplete()
    } else {
        complianceProfileComplete.value = !(await isEcspComplianceProfileInformationMissingForInvestment())
    }
}

function setFormErrors() {
    Object.keys(getLegalAccountValidationSchema(true)).forEach(async (formSchema) => {
        const currentAccountValidationSchema = getLegalAccountValidationSchema(true)[formSchema]

        const form = useForm({
            validationSchema: currentAccountValidationSchema,
            initialValues: { legal_account: legalAccount },
            validateOnMount: true,
        })
        const validation = await form.validate()
        formErrors.value[formSchema] = validation
    })
}

function hasPageErrors(page: LegalAccountPage) {
    if (page === 'Verification') {
        return !legalAccountState.isVerified
    }
    if (page === 'Signatures') {
        // Check if legal Signatures or beneficial owners signatures are not signed
        return (
            !!legalAccountState.legalProfile.signatures.find((signature) => signature.signed_at === null) ||
            !!legalAccountState.legalProfile.beneficial_owners.find(
                (beneficialOwner) => !!beneficialOwner.signatures.find((signature) => signature.signed_at === null)
            )
        )
    }
    if (page === 'Compliance Profile') {
        return !complianceProfileComplete.value
    }

    return formErrors.value[page] && !formErrors.value[page].valid
}

const shownNavigationItems = computed(() => {
    return navigationItemsLegal.map((item) => {
        if (item.hasOwnProperty('children')) {
            item.children = item.children.filter((child) => {
                return (
                    !getHiddenLegalNavigationSections().includes(child.component) &&
                    !(child.component === 'Compliance Profile' && !getPlatformSettings().compliance_profile)
                )
            })
        }
        return item
    })
})

const shownAccountLinksLegal = computed(() => {
    return accountLinksLegal.map((item) => {
        if (item.hasOwnProperty('children')) {
            item.children = item.children.filter((child) => {
                // TODO: Fix this check
                if (!showWalletLink() && child.name === i18n.global.t('account_links.wallet')) {
                    return false
                }
                // TODO: Fix this check too :=)
                if (!showOrdersLink() && child.name === i18n.global.t('account_links.orders')) {
                    return false
                }
                return true
            })
        }
        return item
    })
})

const showAccountLinksLegal = computed(() => {
    return showWalletLink() || showOrdersLink()
})

watch(
    () => legalAccountState.saving,
    (val) => {
        updateComplianceProfileIsComplete()
        if (!val) setFormErrors()
    }
)
const emit = defineEmits(['navigate'])
</script>

<template>
    <div class="flex flex-shrink-0 items-center space-x-4 px-4 pb-4">
        <div class="flex items-center justify-center rounded-full border-2 border-gray-300 bg-white p-3">
            <BriefcaseIcon class="border-gray-150 w-6" />
        </div>
        <div>
            <div class="text-xs font-bold uppercase text-gray-600">{{ $t('legal_account.company_profile') }}</div>
            <div class="text-md uppercase">{{ legalAccount.company_name }}</div>
        </div>
    </div>

    <div class="border-gray-150 flex flex-grow flex-col overflow-y-auto rounded-lg border bg-white py-4 shadow-md">
        <div class="lex flex-grow flex-col">
            <nav class="flex-1 space-y-1 bg-white px-2" aria-label="Sidebar">
                <AccountNavigation
                    :navigation-items="shownNavigationItems"
                    :has-page-errors="hasPageErrors"
                    :current-page="navigationState.page"
                    :navigate-to="navigateTo"
                    @navigate="emit('navigate')"></AccountNavigation>
            </nav>
        </div>
    </div>

    <div
        v-if="showAccountLinksLegal"
        class="border-gray-150 mt-7 flex flex-grow flex-col overflow-y-auto rounded-lg border bg-white pb-4 pt-2 shadow-md">
        <div class="lex flex-grow flex-col">
            <nav class="flex-1 space-y-1 bg-white px-2" aria-label="Sidebar">
                <AccountNavigation
                    :navigation-items="shownAccountLinksLegal"
                    :has-page-errors="hasPageErrors"
                    :current-page="navigationState.page"
                    :navigate-to="navigateTo"></AccountNavigation>
            </nav>
        </div>
    </div>
</template>

<style scoped></style>
